import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useUpdateEffect } from "react-use";
import { Input, Button, Tooltip, InfoIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import { showErrorToast } from "~/components/Toast";
import { ADD_PROJECT_TEMPLATE_ELEMENT_SUCCESS } from "~/store/action_types";
import { addProjectTemplateElement } from "~/store/actions/actions.projectTemplateElements";
import { keyCodes } from "~/utils/key_codes.jsx";
import { TemplateSubtaskAssigneePicker } from "./TemplateSubtaskAssigneePicker";
import { TemplateSubtaskDueOnForm } from "./TemplateSubtaskDueOnForm";
import { PROJECT_TEMPLATE_SUBTASK } from "../../utils";

const TemplateSubtaskForm = ({ templateId, taskId, isHiddenFromClients, handleClose }) => {
  const inputRef = useRef();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initialData = useMemo(
    () => ({
      body: "",
      assignee_id: 0,
      due_on: 0,
      type: PROJECT_TEMPLATE_SUBTASK,
      task_id: taskId,
    }),
    [taskId]
  );
  const [values, setValues] = useState(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = useCallback(() => {
    if (!values.body.trim()) {
      inputRef.current.focus();
      return setError(t("Name is required"));
    }

    setIsSubmitting(true);
    dispatch(addProjectTemplateElement(templateId, values))
      .then((response) => {
        if (response.type === ADD_PROJECT_TEMPLATE_ELEMENT_SUCCESS) {
          return setValues(initialData);
        }
        showErrorToast(t("Unable to add subtask."));
      })
      .finally(() => {
        setIsSubmitting(false);
        inputRef.current.focus();
      });
  }, [dispatch, values, initialData, t, templateId]);

  const handleInputKeyUp = useCallback(
    (e) => {
      if (e.key === keyCodes.escape && typeof handleClose === "function") {
        e.stopPropagation();
        handleClose();
      }
    },
    [handleClose]
  );

  const handleOnKeyDown = useCallback(
    (e) => {
      if (e.key === keyCodes.enter) {
        return handleSubmit();
      }

      if (e.key === keyCodes.escape && typeof handleClose === "function") {
        e.stopPropagation();
        handleClose();
      }
    },
    [handleClose, handleSubmit]
  );

  const handleInputChange = useCallback((e) => {
    if (e) {
      const name = e.target.value;
      setValues((prevState) => ({ ...prevState, body: name }));
      setError("");
    }
  }, []);

  const handlePropertiesChange = useCallback((data) => {
    setValues((prevState) => ({ ...prevState, ...data }));
  }, []);

  useUpdateEffect(() => {
    if (isHiddenFromClients) {
      setValues((prevState) => ({ ...prevState, assignee_id: 0 }));
    }
  }, [isHiddenFromClients]);

  return (
    <div className="tw-flex-col tw-p-2 tw-bg-theme-200 tw-rounded-lg">
      <div className="tw-flex tw-items-center tw-gap-2">
        <Input
          ref={inputRef}
          type="text"
          onKeyUp={handleInputKeyUp}
          onKeyDown={handleOnKeyDown}
          placeholder={t("Name of the subtask")}
          value={values.body}
          onChange={handleInputChange}
          autoComplete="off"
          autoFocus
          className="tw-h-10 tw-flex-1"
          invalid={!!error}
          endAdornment={
            error ? (
              <Tooltip title={error}>
                <InfoIcon
                  fill="var(--red-alert)"
                  className="tw-cursor-help tw-mt-0.5 tw-mr-1"
                />
              </Tooltip>
            ) : null
          }
        />
        <TemplateSubtaskDueOnForm
          dueOn={+values.due_on}
          onChange={handlePropertiesChange}
        />
        <TemplateSubtaskAssigneePicker
          subtask={{ template_id: templateId, assignee_id: values.assignee_id }}
          isHiddenFromClients={isHiddenFromClients}
          onChange={handlePropertiesChange}
        />
      </div>
      <div className="tw-flex tw-items-center tw-mt-2">
        <Button
          type="button"
          variant="primary"
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {t("Add")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="tw-ml-2"
          onClick={handleClose}
        >
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
};

TemplateSubtaskForm.propTypes = {
  templateId: PropTypes.number.isRequired,
  taskId: PropTypes.number.isRequired,
  isHiddenFromClients: PropTypes.bool,
  handleClose: PropTypes.func,
};

TemplateSubtaskForm.displayName = "TemplateSubtaskForm";

export default TemplateSubtaskForm;
